import { useState, useEffect } from 'react';
import { Panel, Loader, Notice, Button } from '@origo/origo-ui';
import Eiendommer, { type EiendommerSummary } from './Eiendommer';
import type { EiendomsvelgerTarget } from './eiendomsvelgerTarget';
import styles from './EiendomsvelgerPanel.module.css';

interface EiendomsvelgerPanelProps {
    /** Målet velgeren opererer på. `null` betyr «ingen avgrensning funnet». */
    target: EiendomsvelgerTarget | null;
    /** Sant mens avgrensningen (typisk plangeometrien) fortsatt lastes. */
    loading?: boolean;
    eiendommerUrl?: string;
    onClose: () => void;
}

const nf = new Intl.NumberFormat('nb-NO');

/** Endrings-oppsummering for footeren. Endringer lagres fortløpende (auto-
 *  persist), så teksten viser hva saksbehandleren har gjort i denne økten. */
function summaryText(summary: EiendommerSummary | null): string {
    // null = laster (Eiendommer rapporterer null mens lasten pågår).
    if (!summary) return 'Laster inn…';
    if (summary.saving) return 'Lagrer…';
    const { total, removed } = summary;
    const inkludert = `${nf.format(total)} ${total === 1 ? 'eiendom' : 'eiendommer'}`;
    // Fjernede vises fordi de nå kan legges tilbake — tallet dekker også
    // fjerninger gjort i tidligere økter.
    return removed > 0 ? `${inkludert} · ${nf.format(removed)} fjernet` : inkludert;
}

/**
 * Eiendomsvelger som eget panel i hovedkolonnen — den delte map-first-visningen
 * for plan/delområde/delområdegruppe. Eiendommene er funnet automatisk fra
 * planområdet; saksbehandleren ser over og justerer ved å klikke teiger i kartet
 * eller fjerne via lista. Endringer lagres fortløpende.
 */
export function EiendomsvelgerPanel({
    target,
    loading = false,
    eiendommerUrl,
    onClose,
}: Readonly<EiendomsvelgerPanelProps>) {
    const [summary, setSummary] = useState<EiendommerSummary | null>(null);

    // Esc lukker velgeren (matcher hint-pillen på kartet).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <Panel
            footer={
                <div className={styles.footer}>
                    <span className={styles.summary} aria-live="polite">{summaryText(summary)}</span>
                    <div className={styles.footerButtons}>
                        <Button variant="primary" onClick={onClose}>
                            Ferdig
                        </Button>
                    </div>
                </div>
            }
        >
            <div className={styles.header}>
                <h2 className={styles.heading}>Inkluderte eiendommer</h2>
                <p className={styles.description}>
                    Her ser du alle eiendommene som er inkludert i planen. Du kan
                    fjerne eller legge til eiendommer manuelt. Fjernede adresser blir
                    stående nedtonet, og kan legges tilbake med «+».
                </p>
            </div>

            {loading ? (
                <div className={styles.loading}>
                    <Loader size="sm" />
                    <span>Laster avgrensning…</span>
                </div>
            ) : target ? (
                <Eiendommer
                    target={target}
                    eiendommerUrl={eiendommerUrl}
                    showEmptyState
                    onSummaryChange={setSummary}
                />
            ) : (
                <Notice variant="info">
                    Fant ingen geografisk avgrensning for dette nivået, så eiendommer kan ikke velges her.
                </Notice>
            )}
        </Panel>
    );
}
